import React, { useCallback, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Link } from "react-router-dom";
import { ImageService } from "../content/image-service";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import { FiShield, FiRefreshCw, FiAward } from "react-icons/fi";

const trustBadges = [
  { icon: FiAward, label: "Premium Quality", sub: "Built to last" },
  { icon: FiShield, label: "BPA Free", sub: "Safe for your family" },
  { icon: FiRefreshCw, label: "Easy Returns", sub: "Hassle-free returns" },
];

const HeroCarousel = ({ bannerImages, imageLoading, className }) => (
  imageLoading && bannerImages.length === 0 ? (
    <Skeleton height="100%" containerClassName={`block h-full ${className || ""}`} />
  ) : (
    <Swiper
      modules={[Navigation, Autoplay, Pagination]}
      loop={true}
      autoplay={{ delay: 5000, disableOnInteraction: false }}
      speed={2000}
      spaceBetween={0}
      slidesPerView={1}
      navigation={false}
      pagination={{ clickable: true }}
      className={`hero_banner h-full ${className || ""}`}
    >
      {bannerImages.map((card, index) => (
        <SwiperSlide key={index}>
          <div className="flex flex-col justify-end relative w-100 h-full">
            <img
              className="w-full absolute h-full object-cover top-0 left-0"
              src={card.image_path}
              alt={card.name}
            />
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  )
);

const HeroSection = () => {

  const [bannerImages, setBannerImages] = useState([]);
  const [imageLoading, setImageLoading] = useState(false);

  const fetchBannerImages = useCallback(async () => {
    setImageLoading(true);
    try {
      const res = await ImageService.getAll({ image_type: 'banner', page_key: 'home' });
      const activeImages = (res.data.data || []).filter((img) => img.is_active);
      setBannerImages(activeImages);
    } catch (err) {
      toast.error('Failed to load images.');
    }
    setImageLoading(false);
  }, []);

  useEffect(() => {
    fetchBannerImages();
  }, [fetchBannerImages]);

  return (
    <>
      {/* ── Desktop (unchanged) ── */}
      <div className="hidden md:block relative w-full h-[500px] overflow-hidden">
        <HeroCarousel bannerImages={bannerImages} imageLoading={imageLoading} />

        {/* Text content, aligned to the same max-w-7xl grid as the header above it.
            pointer-events-none on the full-bleed wrapper so it doesn't block the
            Swiper pagination dots underneath; re-enabled on the actual content. */}
        <div className="flex absolute inset-0 items-center z-20 pointer-events-none">
          <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 max-w-lg pointer-events-auto">
              <span className="text-xs font-semibold uppercase tracking-wide text-brand-600">
                Premium Quality &bull; Made for Everyday
              </span>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                Designed for <span className="text-brand-600">Everyday</span> Living.
              </h1>
              <p className="text-sm text-gray-600">
                Smart, durable and beautifully designed plastic products for every space.
              </p>
              <div className="flex flex-nowrap gap-3">
                <Link
                  to="/products"
                  className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-6 py-3 rounded-lg whitespace-nowrap transition-colors"
                >
                  Shop Collection &rarr;
                </Link>
                <a
                  href="#shop-by-category"
                  className="bg-white border border-gray-200 hover:border-brand-600 text-gray-800 text-sm font-semibold px-6 py-3 rounded-lg whitespace-nowrap transition-colors"
                >
                  Explore Categories
                </a>
              </div>
              <div className="flex flex-nowrap gap-4 mt-1">
                {trustBadges.map(({ icon: Icon, label, sub }) => (
                  <div key={label} className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-white/70 flex items-center justify-center shrink-0">
                      <Icon className="text-brand-600" size={14} />
                    </span>
                    <div className="leading-tight whitespace-nowrap">
                      <p className="text-[11px] font-semibold text-gray-800">{label}</p>
                      <p className="text-[9px] text-gray-500">{sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile: image + text stacked in one cream card ── */}
      <div className="md:hidden bg-cream-50 rounded-2xl mx-4 mt-1 overflow-hidden">
        <div className="relative w-full h-[200px]">
          <HeroCarousel bannerImages={bannerImages} imageLoading={imageLoading} />
        </div>
        <div className="flex flex-col gap-3 px-5 py-5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-brand-600">
            Premium Quality
          </span>
          <h1 className="text-2xl font-bold text-gray-900 leading-tight">
            Designed for <span className="text-brand-600">Everyday</span> Living.
          </h1>
          <p className="text-xs text-gray-600">
            Smart, durable and beautifully designed plastic products for every space.
          </p>
          <div className="flex flex-wrap gap-2.5 mt-1">
            <Link
              to="/products"
              className="flex-1 text-center bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold px-4 py-2.5 rounded-lg whitespace-nowrap transition-colors"
            >
              Shop Collection &rarr;
            </Link>
            <a
              href="#shop-by-category"
              className="flex-1 text-center bg-white border border-gray-200 text-gray-800 text-xs font-semibold px-4 py-2.5 rounded-lg whitespace-nowrap transition-colors"
            >
              Explore Categories
            </a>
          </div>
          <div className="flex justify-between gap-2 mt-2 pt-3 border-t border-gray-100">
            {trustBadges.map(({ icon: Icon, label, sub }) => (
              <div key={label} className="flex flex-col items-center text-center gap-1 flex-1">
                <Icon className="text-brand-600" size={16} />
                <p className="text-[10px] font-semibold text-gray-800 leading-tight">{label}</p>
                <p className="text-[8px] text-gray-500 leading-tight">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default HeroSection;
